import { sharedFileRequestsApi } from "@api";
import {
  RequirementType,
  SharedFileRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesSharedFileRequests } from "../../../../v3/query/queriesSharedFileRequests";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";

import { getSharedFileIsValidatingActive } from "./configs/helpers/getSharedFileIsValidatingActive";
import { getSharedFileRequestFromCache } from "./configs/helpers/getSharedFileRequestFromCache";
import { usePayeeSharedFileRequirementConfig } from "./configs/payee/usePayeeSharedFileRequirementConfig";
import { usePayerSharedFileRequirementConfig } from "./configs/payer/usePayerSharedFileRequirementConfig";

/**
 * Hoisted so the batch read and `buildConfigFields` address the same cache
 * entry — the config resolves a missing document's pending/failed state through
 * this key.
 */
const getDataSourceQueryKey = (id: string) =>
  queriesSharedFileRequests.getSharedFileRequest.getQueryKey({
    sharedFileRequestId: id
  });

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    getDataSourceQueryKey,
    requirements,
    id =>
      sharedFileRequestsApi.getSharedFileRequest({ sharedFileRequestId: id }),
    RequirementType.DOCUMENT_UPLOAD,
    // An uploaded file stays in AI validation after the upload mutation
    // resolves; keep re-reading until it leaves `Processing`, otherwise the
    // "validating" widget never advances on its own.
    getSharedFileIsValidatingActive
  );

  return useWSQueries({ queries });
};

export const useSharedFileRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.DOCUMENT_UPLOAD] => {
  const contextQueries = useContextQueries(requirements);

  const payerSharedFileRequirementConfig =
    usePayerSharedFileRequirementConfig(callback);
  const payeeSharedFileRequirementConfig =
    usePayeeSharedFileRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // recompute only when the batch query data updates — the payer/payee configs
  // are new objects every render and must not invalidate the memo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<SharedFileRequest, RequirementType.DOCUMENT_UPLOAD>({
      payerConfig: payerSharedFileRequirementConfig,
      payeeConfig: payeeSharedFileRequirementConfig,
      getAdditionalData: getSharedFileRequestFromCache,
      hasDataSource: true,
      getDataSourceQueryKey
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSharedFileRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
